import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import {
  isWebAuthnSupported,
  prepareRegistrationOptions,
  formatRegistrationCredential
} from "../utils/webauthn";


const navItems = [
  { to: "/",        icon: "🏠", label: "Dashboard",  exact: true, pageKey: "dashboard" },
  { to: "/campers", icon: "👤", label: "Campers", pageKey: "campers" },
  { to: "/teams",   icon: "🏆", label: "Teams", pageKey: "teams" },
  { to: "/checkin", icon: "✅", label: "Check-In", pageKey: "checkin" },
  { to: "/cabins",  icon: "⛺", label: "Cabins", pageKey: "cabins" },
  { to: "/app/schedule", icon: "📅", label: "Schedule", pageKey: "schedule" },
  { to: "/retreat-ops", icon: "⛺", label: "Retreat Ops", pageKey: "retreat_ops" },
  { to: "/kidz-corner", icon: "🧸", label: "Kidz Corner", pageKey: "kidz_corner" },
  { to: "/outdoor", icon: "🛶", label: "Outdoor Activities", pageKey: "outdoor" },
  { to: "/tshirts", icon: "👕", label: "Apparel", pageKey: "apparel" },
  { to: "/camp-info", icon: "ℹ️", label: "Camp Info", pageKey: "camp_info" },
];

const adminItems = [
  { to: "/finance", icon: "💰", label: "Finance", pageKey: "finance" },
  { to: "/users",   icon: "⚙️", label: "Users", pageKey: "users" },
  { to: "/logs",    icon: "📄", label: "Audit Logs", pageKey: "logs" },
  { to: "/role-assigner", icon: "🛡️", label: "Role Assigner", pageKey: "role_assigner" },
  { to: "/registration-config", icon: "📝", label: "Registration Config", pageKey: "registration_config" },
];

function NavItem({ to, icon, label, exact }) {
  const location = useLocation();
  const active = exact ? location.pathname === to : location.pathname.startsWith(to);
  return (
    <NavLink
      to={to}
      className={`nav-item${active ? " active" : ""}`}
    >
      <span className="icon">{icon}</span>
      {label}
    </NavLink>
  );
}

export default function AppShell() {
  const { user, setUser, logout, hasPermission } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const location = useLocation();

  // Force Password Change States
  const [forceNewPassword, setForceNewPassword] = useState("");
  const [forceConfirmPassword, setForceConfirmPassword] = useState("");
  const [forcePwdError, setForcePwdError] = useState("");
  const [forcePwdSaving, setForcePwdSaving] = useState(false);

  const handleForcePasswordSubmit = async (e) => {
    e.preventDefault();
    setForcePwdError("");
    if (forceNewPassword !== forceConfirmPassword) {
      setForcePwdError("Passwords do not match.");
      return;
    }
    const validatePassword = (pwd) => {
      if (pwd.length < 8) return "Password must be at least 8 characters long.";
      if (!/[A-Z]/.test(pwd)) return "Password must contain at least one uppercase letter.";
      if (!/[a-z]/.test(pwd)) return "Password must contain at least one lowercase letter.";
      if (!/[0-9]/.test(pwd)) return "Password must contain at least one number.";
      if (!/[@$!%*?&#^\-+=_]/.test(pwd)) return "Password must contain at least one special character (@$!%*?&#^-+=_).";
      return null;
    };
    const errStr = validatePassword(forceNewPassword);
    if (errStr) {
      setForcePwdError(errStr);
      return;
    }
    setForcePwdSaving(true);
    try {
      const res = await api.post("/api/auth/force-change-password", { new_password: forceNewPassword });
      setUser(res.data.user);
      setForceNewPassword("");
      setForceConfirmPassword("");
    } catch (err) {
      setForcePwdError(err.response?.data?.error || "Failed to update password.");
    } finally {
      setForcePwdSaving(false);
    }
  };

  // Change Password States
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }
    const validatePassword = (pwd) => {
      if (pwd.length < 8) return "Password must be at least 8 characters long.";
      if (!/[A-Z]/.test(pwd)) return "Password must contain at least one uppercase letter.";
      if (!/[a-z]/.test(pwd)) return "Password must contain at least one lowercase letter.";
      if (!/[0-9]/.test(pwd)) return "Password must contain at least one number.";
      if (!/[@$!%*?&#^\-+=_]/.test(pwd)) return "Password must contain at least one special character (@$!%*?&#^-+=_).";
      return null;
    };
    const errStr = validatePassword(newPassword);
    if (errStr) {
      setPasswordError(errStr);
      return;
    }

    setPasswordSaving(true);
    try {
      await api.post("/api/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword
      });
      setPasswordSuccess("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        setIsChangePasswordOpen(false);
        setPasswordSuccess("");
      }, 1500);
    } catch (err) {
      setPasswordError(err.response?.data?.error || "Failed to change password.");
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!await window.confirm("WARNING: Are you sure you want to permanently delete your account? This action is irreversible and all your data will be permanently removed from the system.")) {
      return;
    }
    try {
      await api.delete("/api/users/delete-me");
      await alert("Your account has been deleted successfully.");
      logout();
    } catch (err) {
      await alert(err.response?.data?.error || "Failed to delete account.");
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      await alert("Image size should be less than 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      try {
        const res = await api.post("/api/users/profile/picture", { profile_picture: base64String });
        setUser(prev => ({
          ...prev,
          profile_picture: res.data.profile_picture
        }));
      } catch (err) {
        await alert(err.response?.data?.error || "Failed to upload profile picture.");
      }
    };
    reader.readAsDataURL(file);
  };

  // Passkeys States
  const [isPasskeysOpen, setIsPasskeysOpen] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [newPasskeyName, setNewPasskeyName] = useState("");
  const [passkeyError, setPasskeyError] = useState("");
  const [passkeySuccess, setPasskeySuccess] = useState("");
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const fetchPasskeys = async () => {
    try {
      const res = await api.get("/api/auth/passkeys");
      setPasskeys(res.data.passkeys || []);
    } catch (err) {
      setPasskeyError("Failed to fetch passkeys.");
    }
  };

  useEffect(() => {
    if (isPasskeysOpen) {
      fetchPasskeys();
      setNewPasskeyName("");
      setPasskeyError("");
      setPasskeySuccess("");
    }
  }, [isPasskeysOpen]);

  const handleRegisterPasskey = async (e) => {
    e.preventDefault();
    setPasskeyError("");
    setPasskeySuccess("");
    setPasskeyLoading(true);

    if (!isWebAuthnSupported()) {
      setPasskeyError("Passkeys/WebAuthn are not supported on this browser or device.");
      setPasskeyLoading(false);
      return;
    }

    try {
      const optionsRes = await api.post("/api/auth/register-passkey/options");
      const options = optionsRes.data;

      const preparedOptions = prepareRegistrationOptions(options);

      const credential = await navigator.credentials.create({
        publicKey: preparedOptions,
      });

      if (!credential) {
        throw new Error("No credential returned from device.");
      }

      const formatted = formatRegistrationCredential(credential);

      await api.post("/api/auth/register-passkey/verify", {
        credential: formatted,
        challenge: options.challenge,
        name: newPasskeyName || "My Passkey",
      });

      setPasskeySuccess("Passkey registered successfully!");
      setNewPasskeyName("");
      fetchPasskeys();
    } catch (err) {
      console.error(err);
      let errMsg = err.response?.data?.error || err.message || "Failed to register passkey.";
      const rawErr = ((err.message || "") + " " + (err.name || "") + " " + String(err)).toLowerCase();
      if (
        err.name === "NotAllowedError" ||
        err.name === "AbortError" ||
        rawErr.includes("privacy-considerations-client") ||
        rawErr.includes("timed out") ||
        rawErr.includes("not allowed")
      ) {
        errMsg = "Passkey operation was cancelled or timed out. Please try again.";
      }
      setPasskeyError(errMsg);
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleDeletePasskey = async (passkeyId) => {
    if (!await window.confirm("Are you sure you want to delete this passkey?")) return;
    setPasskeyError("");
    setPasskeySuccess("");
    try {
      await api.delete(`/api/auth/passkeys/${passkeyId}`);
      setPasskeySuccess("Passkey deleted successfully!");
      fetchPasskeys();
    } catch (err) {
      setPasskeyError(err.response?.data?.error || "Failed to delete passkey.");
    }
  };


  // Close mobile drawer when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const initials = user?.full_name
    ? user.full_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : user?.username?.slice(0, 2).toUpperCase();

  return (
    <div className="app-shell">
      {/* Mobile Sticky Header */}
      <header className="mobile-header">
        <Link to="/" className="mobile-brand" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none", color: "inherit" }}>
          <img src="/gca-logo-white.png" alt="GCA Logo" style={{ height: 22, width: "auto", objectFit: "contain" }} />
          <span style={{ fontSize: "0.9rem", fontWeight: 700, letterSpacing: "0.3px" }}>Camp Manager</span>
        </Link>
        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle Navigation Menu"
        >
          {isMobileMenuOpen ? "✕" : "☰"}
        </button>
      </header>

      {/* Backdrop overlay for closing the drawer */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-backdrop" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      <aside className={`sidebar ${isMobileMenuOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-brand" style={{ paddingBottom: 16 }}>
          <Link to="/" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", textDecoration: "none", color: "inherit" }}>
            <img src="/gca-logo-white.png" alt="GCA Logo" style={{ width: 140, height: "auto", marginBottom: 10, objectFit: "contain", cursor: "pointer" }} />
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "rgba(255, 255, 255, 0.7)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Camp Registration</span>
          </Link>
        </div>

        <nav className="sidebar-nav">
          {/* Main / Overview */}
          {navItems
            .filter(item => item.pageKey === "dashboard")
            .filter(item => hasPermission(item.pageKey, "hide"))
            .map(item => (
              <NavItem key={item.to} {...item} />
            ))
          }

          {/* Rosters & Logistics */}
          {navItems.some(item => ["campers", "teams", "checkin", "cabins"].includes(item.pageKey) && hasPermission(item.pageKey, "hide")) && (
            <>
              <div style={{ fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.35)", padding: "10px 12px 2px" }}>Rosters & Logistics</div>
              {navItems
                .filter(item => ["campers", "teams", "checkin", "cabins"].includes(item.pageKey))
                .filter(item => hasPermission(item.pageKey, "hide"))
                .map(item => (
                  <NavItem key={item.to} {...item} />
                ))
              }
            </>
          )}

          {/* Operations */}
          {navItems.some(item => ["schedule", "retreat_ops", "apparel", "camp_info"].includes(item.pageKey) && hasPermission(item.pageKey, "hide")) && (
            <>
              <div style={{ fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.35)", padding: "10px 12px 2px" }}>Operations</div>
              {navItems
                .filter(item => ["schedule", "retreat_ops", "apparel", "camp_info"].includes(item.pageKey))
                .filter(item => hasPermission(item.pageKey, "hide"))
                .map(item => (
                  <NavItem key={item.to} {...item} />
                ))
              }
            </>
          )}

          {/* Activities */}
          {navItems.some(item => ["kidz_corner", "outdoor"].includes(item.pageKey) && hasPermission(item.pageKey, "hide")) && (
            <>
              <div style={{ fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.35)", padding: "10px 12px 2px" }}>Activities</div>
              {navItems
                .filter(item => ["kidz_corner", "outdoor"].includes(item.pageKey))
                .filter(item => hasPermission(item.pageKey, "hide"))
                .map(item => (
                  <NavItem key={item.to} {...item} />
                ))
              }
            </>
          )}

          {/* Admin Section */}
          {(adminItems.some(item => hasPermission(item.pageKey, "hide")) || (!hasPermission("finance", "hide") && hasPermission("receipt_upload", "hide"))) && (
            <>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", margin: "8px 0" }} />
              <div style={{ fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.35)", padding: "4px 12px 2px" }}>Admin</div>
              {adminItems
                .filter(item => hasPermission(item.pageKey, "hide"))
                .map(item => (
                  <NavItem key={item.to} {...item} />
                ))
              }
              {!hasPermission("finance", "hide") && hasPermission("receipt_upload", "hide") && (
                <NavItem to="/finance?tab=expenses" icon="🧾" label="Submit Receipts" />
              )}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-badge">
            <label htmlFor="avatar-upload" className="avatar" title="Click to upload profile picture">
              {user?.profile_picture ? (
                <img src={user.profile_picture} alt={user.full_name || user.username} />
              ) : (
                initials
              )}
            </label>
            <input
              type="file"
              id="avatar-upload"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: "none" }}
            />
            <div className="info">
              <div className="name">{user?.full_name || user?.username}</div>
              <div className="role-tag">{user?.role}</div>
            </div>
          </div>
          <button className="nav-item w-full" onClick={() => setIsSettingsOpen(!isSettingsOpen)} style={{ marginBottom: 4 }}>
            <span className="icon">⚙️</span>
            Settings
            <span style={{ marginLeft: "auto", fontSize: "0.75rem", transform: isSettingsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▼</span>
          </button>
          {isSettingsOpen && (
            <div style={{ paddingLeft: 12, display: "flex", flexDirection: "column", gap: 2, marginBottom: 4 }}>
              <button className="nav-item w-full" onClick={() => setIsChangePasswordOpen(true)} style={{ fontSize: "0.825rem", padding: "8px 10px" }}>
                <span className="icon">🔑</span>
                Change Password
              </button>
              <button className="nav-item w-full" onClick={() => setIsPasskeysOpen(true)} style={{ fontSize: "0.825rem", padding: "8px 10px" }}>
                <span className="icon">🛡️</span>
                Manage Passkeys
              </button>
              <button className="nav-item w-full" onClick={handleDeleteAccount} style={{ fontSize: "0.825rem", padding: "8px 10px", color: "var(--danger)" }}>
                <span className="icon">🗑️</span>
                Delete Account
              </button>
            </div>
          )}
          <button className="nav-item w-full" onClick={logout}>
            <span className="icon">🚪</span>
            Sign Out
          </button>
        </div>
      </aside>

      <div className="main-content">
        <Outlet />
      </div>

      {/* Change Password Modal */}
      {isChangePasswordOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button className="modal-close" onClick={() => {
                setIsChangePasswordOpen(false);
                setPasswordError("");
                setPasswordSuccess("");
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
              }}>×</button>
            </div>
            
            <form onSubmit={handleChangePassword}>
              {passwordError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{passwordError}</div>}
              {passwordSuccess && <div className="alert alert-success" style={{ marginBottom: 12 }}>{passwordSuccess}</div>}
              
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Current Password *</label>
                <input 
                  type="password" 
                  className="form-input" 
                  value={currentPassword} 
                  onChange={e => setCurrentPassword(e.target.value)} 
                  required 
                />
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">New Password *</label>
                <input 
                  type="password" 
                  className="form-input" 
                  value={newPassword} 
                  onChange={e => setNewPassword(e.target.value)} 
                  required 
                />
                <span style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: 4, display: "block" }}>
                  Password must be at least 8 characters and contain an uppercase letter, a lowercase letter, a number, and a special character (@$!%*?&#^-+=_).
                </span>
              </div>

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Confirm New Password *</label>
                <input 
                  type="password" 
                  className="form-input" 
                  value={confirmPassword} 
                  onChange={e => setConfirmPassword(e.target.value)} 
                  required 
                />
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-ghost" 
                  disabled={passwordSaving}
                  onClick={() => {
                    setIsChangePasswordOpen(false);
                    setPasswordError("");
                    setPasswordSuccess("");
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={passwordSaving}>
                  {passwordSaving ? "Saving..." : "Change Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Passkeys Modal */}
      {isPasskeysOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2>Manage Biometric Passkeys</h2>
              <button className="modal-close" onClick={() => setIsPasskeysOpen(false)}>×</button>
            </div>
            
            <div className="modal-body" style={{ padding: "0 20px 20px" }}>
              {passkeyError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{passkeyError}</div>}
              {passkeySuccess && <div className="alert alert-success" style={{ marginBottom: 12 }}>{passkeySuccess}</div>}
              
              {!isWebAuthnSupported() && (
                <div className="alert alert-error" style={{ marginBottom: 12 }}>
                  ⚠️ Your browser or device does not support WebAuthn / Passkeys, or you are not using a secure (HTTPS/localhost) connection.
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: "0.95rem", marginBottom: 10, color: "#374151" }}>Registered Passkeys</h3>
                {passkeys.length === 0 ? (
                  <p style={{ fontSize: "0.85rem", color: "#6B7280", fontStyle: "italic", background: "#F9FAFB", padding: 12, borderRadius: 6, border: "1px dashed #E5E7EB" }}>
                    No biometric passkeys registered. Register your device below to log in faster.
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {passkeys.map(pk => (
                      <div key={pk.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#F9FAFB", padding: "10px 12px", borderRadius: 6, border: "1px solid #E5E7EB" }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#1F2937" }}>🛡️ {pk.name || "Unnamed Passkey"}</div>
                          <div style={{ fontSize: "0.75rem", color: "#6B7280" }}>
                            Registered: {pk.created_at ? new Date(pk.created_at).toLocaleDateString() : "Unknown"} • Used {pk.sign_count} times
                          </div>
                        </div>
                        <button 
                          className="btn btn-ghost" 
                          style={{ padding: "4px 8px", color: "#EF4444" }} 
                          onClick={() => handleDeletePasskey(pk.id)}
                          title="Delete Passkey"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {isWebAuthnSupported() && (
                <form onSubmit={handleRegisterPasskey} style={{ borderTop: "1px solid #E5E7EB", paddingTop: 16 }}>
                  <h3 style={{ fontSize: "0.95rem", marginBottom: 10, color: "#374151" }}>Register This Device</h3>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Passkey Name *</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder="e.g. Face ID / Touch ID, My Laptop"
                      value={newPasskeyName} 
                      onChange={e => setNewPasskeyName(e.target.value)} 
                      required 
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    className="btn btn-primary w-full" 
                    style={{ justifyContent: "center", gap: 8 }}
                    disabled={passkeyLoading}
                  >
                    {passkeyLoading ? (
                      <>
                        <span className="spinner" /> Authenticating...
                      </>
                    ) : (
                      <>
                        <span>🔑</span> Register Biometric Passkey
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-ghost" 
                onClick={() => setIsPasskeysOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mandatory Force Password Change Modal */}
      {user?.must_change_password && (
        <div className="modal-overlay" style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0, 0, 0, 0.75)", backdropFilter: "blur(4px)", display: "flex",
          justifyContent: "center", alignItems: "center", zIndex: 9999
        }}>
          <div className="modal-card" style={{
            background: "#fff", padding: 28, borderRadius: 12,
            width: "90%", maxWidth: 450, boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
          }}>
            <h2 style={{ margin: "0 0 8px 0", color: "var(--forest)", fontSize: "1.35rem", display: "flex", alignItems: "center", gap: 8 }}>
              🔑 Mandatory Password Change Required
            </h2>
            <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginBottom: 20 }}>
              An administrator has requested that you change your password for security before continuing. Please create a new password below.
            </p>

            <form onSubmit={handleForcePasswordSubmit}>
              {forcePwdError && <div className="alert alert-error" style={{ marginBottom: 12, fontSize: "0.825rem" }}>⚠️ {forcePwdError}</div>}

              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">New Password *</label>
                <input 
                  type="password" 
                  className="form-input" 
                  value={forceNewPassword}
                  onChange={e => setForceNewPassword(e.target.value)}
                  placeholder="Enter a strong password"
                  required
                />
                <span style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: 4, display: "block" }}>
                  Password must be at least 8 characters and contain an uppercase letter, a lowercase letter, a number, and a special character (@$!%*?&#^-+=_).
                </span>
              </div>

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Confirm New Password *</label>
                <input 
                  type="password" 
                  className="form-input" 
                  value={forceConfirmPassword}
                  onChange={e => setForceConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                />
              </div>

              <button 
                type="submit" 
                className="btn btn-primary" 
                style={{ width: "100%", padding: "10px", fontSize: "0.95rem" }}
                disabled={forcePwdSaving}
              >
                {forcePwdSaving ? "Updating Password..." : "🔒 Set New Password & Continue"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
